import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  IconButton,
  Paper,
  Card,
  CardContent
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';

// Simplified field definitions
const RULE_FIELDS = {
  // Common order fields
  financial_status: {
    label: 'Payment Status',
    type: 'select',
    options: ['pending', 'paid', 'partially_paid', 'refunded', 'voided']
  },
  status: {
    label: 'Order Status',
    type: 'select',
    options: ['pending', 'sent', 'fulfilled', 'shipped', 'delivered', 'problem', 'hold', 'cancelled', 'failed', 'refunded', 'unfulfilled']
  },
  // Payment and shipping methods
  payment_method: {
    label: 'Payment Method',
    type: 'select',
    options: ['shopify_payments', 'Cash on Delivery (COD)', 'Contrassegno (+5,00€)', 'paypal', 'stripe', 'manual']
  },
  shipping_method: {
    label: 'Shipping Method',
    type: 'select',
    options: ['standard', 'express', 'overnight', 'pickup', 'free_shipping', 'expedited', 'ground', 'air']
  },
  // Order characteristics
  tags: {
    label: 'Order Tags',
    type: 'text',
    placeholder: 'e.g. cod-request, urgent, bulk'
  },
  total_price: {
    label: 'Order Total',
    type: 'number',
    placeholder: 'e.g. 50.00'
  },
  currency: {
    label: 'Currency',
    type: 'select',
    options: ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'SEK', 'NOK', 'DKK']
  },
  // Shipping address fields
  shipping_address_country: {
    label: 'Shipping Country',
    type: 'text',
    placeholder: 'e.g. United States, Canada, Slovenia'
  },
  shipping_address_country_code: {
    label: 'Country Code',
    type: 'select',
    options: ['US', 'CA', 'GB', 'DE', 'FR', 'IT', 'ES', 'AU', 'SI', 'AT', 'CH', 'NL', 'BE', 'SE', 'NO', 'DK']
  },
  shipping_address_province: {
    label: 'State/Province',
    type: 'text',
    placeholder: 'e.g. California, Ontario, Ljubljana'
  },
  shipping_address_city: {
    label: 'City',
    type: 'text',
    placeholder: 'e.g. Los Angeles, Toronto, Ljubljana'
  },
  shipping_address_zip: {
    label: 'Postal Code',
    type: 'text',
    placeholder: 'e.g. 90210, M5V 3A1, 1000'
  },
  // Customer information
  customer_email: {
    label: 'Customer Email',
    type: 'text',
    placeholder: 'e.g. customer@example.com'
  },
  customer_phone: {
    label: 'Customer Phone',
    type: 'text',
    placeholder: 'e.g. +1-555-123-4567'
  },
  customer_order_count: {
    label: 'Customer Order Count',
    type: 'number',
    placeholder: 'e.g. 2 (for repeat customers with 2+ orders)'
  },
  // Product fields
  product_type: {
    label: 'Product Type',
    type: 'text',
    placeholder: 'e.g. Electronics, Clothing, Health'
  },
  vendor: {
    label: 'Vendor',
    type: 'text',
    placeholder: 'e.g. Apple, Nike, Bioceutica'
  },
  sku: {
    label: 'Product SKU',
    type: 'text',
    placeholder: 'e.g. ABC-123, BIO-VIT-001'
  },
  product_title: {
    label: 'Product Title',
    type: 'text',
    placeholder: 'e.g. Vitamin D3, Omega-3'
  },
  product_weight: {
    label: 'Product Weight (grams)',
    type: 'number',
    placeholder: 'e.g. 500'
  },
  // Order timing
  created_at: {
    label: 'Order Date',
    type: 'text',
    placeholder: 'e.g. 2024-01-15 (YYYY-MM-DD)'
  },
  // Special handling
  requires_shipping: {
    label: 'Requires Shipping',
    type: 'select',
    options: ['true', 'false']
  },
  gift_message: {
    label: 'Has Gift Message',
    type: 'select',
    options: ['true', 'false']
  },
  // Business logic
  source_name: {
    label: 'Sales Channel',
    type: 'select',
    options: ['shopify', 'amazon', 'ebay', 'website', 'marketplace', 'wholesale', 'retail']
  },
  order_number: {
    label: 'Order Number',
    type: 'text',
    placeholder: 'e.g. #1001, ORD-2024-001'
  }
};

// Simplified operators
const OPERATORS = {
  text: [
    { value: 'contains', label: 'contains' },
    { value: 'equals', label: 'equals' },
    { value: 'starts_with', label: 'starts with' },
    { value: 'not_contains', label: 'does not contain' },
    { value: 'in', label: 'is one of' }
  ],
  number: [
    { value: 'equals', label: 'equals' },
    { value: 'greater_than', label: 'greater than' },
    { value: 'greater_equal', label: 'greater than or equal' },
    { value: 'less_than', label: 'less than' },
    { value: 'less_equal', label: 'less than or equal' }
  ],
  select: [
    { value: 'equals', label: 'is' },
    { value: 'not_equals', label: 'is not' },
    { value: 'in', label: 'is one of' }
  ]
};

const RuleBuilder = ({ value = [], onChange, ruleType = 'import' }) => {
  const [conditions, setConditions] = useState(
    value.length > 0 ? value : [{ field: '', operator: '', value: '', connector: 'and' }]
  );

  useEffect(() => {
    onChange(conditions);
  }, [conditions, onChange]);

  const addCondition = () => {
    setConditions([...conditions, { field: '', operator: '', value: '', connector: 'and' }]);
  };

  const removeCondition = (index) => {
    if (conditions.length === 1) return;
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const updateCondition = (index, field, value) => {
    const newConditions = [...conditions];
    const oldCondition = newConditions[index];
    newConditions[index] = { ...newConditions[index], [field]: value };
    
    // Reset operator and value when field changes
    if (field === 'field') {
      newConditions[index].operator = '';
      newConditions[index].value = '';
    }
    
    // Handle value type conversion when operator changes
    if (field === 'operator') {
      const oldOperator = oldCondition.operator;
      const newOperator = value;
      
      // Converting from "in" to other operators - take first value from array
      if (oldOperator === 'in' && newOperator !== 'in' && Array.isArray(oldCondition.value)) {
        newConditions[index].value = oldCondition.value[0] || '';
      }
      
      // Converting from other operators to "in" - convert string to array
      if (oldOperator !== 'in' && newOperator === 'in' && typeof oldCondition.value === 'string') {
        newConditions[index].value = oldCondition.value ? [oldCondition.value] : [];
      }
      
      // Reset value if no previous value exists
      if (!oldCondition.value) {
        newConditions[index].value = newOperator === 'in' ? [] : '';
      }
    }
    
    setConditions(newConditions);
  };

  const getFieldType = (fieldName) => {
    return RULE_FIELDS[fieldName]?.type || 'text';
  };

  const getAvailableOperators = (fieldName) => {
    const fieldType = getFieldType(fieldName);
    return OPERATORS[fieldType] || OPERATORS.text;
  };

  const renderValueInput = (condition, index) => {
    const field = RULE_FIELDS[condition.field];
    if (!field) return null;

    // Handle "in" operator with array values
    if (condition.operator === 'in') {
      let displayValue = '';
      if (Array.isArray(condition.value)) {
        displayValue = condition.value.join(', ');
      } else if (typeof condition.value === 'string') {
        displayValue = condition.value;
      }

      return (
        <TextField
          fullWidth
          label="Values (comma-separated)"
          value={displayValue}
          onChange={(e) => {
            const inputValue = e.target.value;
            // Convert comma-separated string to array for storage
            const arrayValue = inputValue.split(',').map(v => v.trim()).filter(v => v);
            updateCondition(index, 'value', arrayValue);
          }}
          placeholder="e.g. biorepair-plus, bioactive-plus, bioregen-plus"
          helperText="Enter multiple values separated by commas"
        />
      );
    }

    if (field.type === 'select') {
      return (
        <FormControl fullWidth>
          <InputLabel>Value</InputLabel>
          <Select
            value={condition.value || ''}
            onChange={(e) => updateCondition(index, 'value', e.target.value)}
            label="Value"
          >
            {field.options.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return (
      <TextField
        fullWidth
        label="Value"
        type={field.type === 'number' ? 'number' : 'text'}
        value={condition.value || ''}
        onChange={(e) => updateCondition(index, 'value', e.target.value)}
        placeholder={field.placeholder}
      />
    );
  };

  // Generate rule preview text
  const generatePreview = () => {
    if (conditions.length === 0 || !conditions[0].field) return 'No conditions set';
    
    return conditions.map((condition, index) => {
      if (!condition.field || !condition.operator) return '';
      
      const field = RULE_FIELDS[condition.field]?.label || condition.field;
      const connector = index > 0 ? ` ${condition.connector.toUpperCase()} ` : '';
      
      let value = '[value]';
      if (condition.value !== undefined && condition.value !== '') {
        if (condition.operator === 'in' && Array.isArray(condition.value)) {
          value = `[${condition.value.join(', ')}]`;
        } else {
          value = condition.value;
        }
      }
      
      return `${connector}${field} ${condition.operator} "${value}"`;
    }).join('');
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Rule Conditions
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Define when this rule should be applied to orders
      </Typography>
      
      {conditions.map((condition, index) => (
        <Card key={index} sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
          <CardContent>
            {/* Logical connector for conditions after the first */}
            {index > 0 && (
              <Box sx={{ mb: 2 }}>
                <FormControl size="small">
                  <InputLabel>Logic</InputLabel>
                  <Select
                    value={condition.connector || 'and'}
                    onChange={(e) => updateCondition(index, 'connector', e.target.value)}
                    label="Logic"
                    sx={{ minWidth: 80 }}
                  >
                    <MenuItem value="and">AND</MenuItem>
                    <MenuItem value="or">OR</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {condition.connector === 'and' ? 'Both conditions must be true' : 'Either condition can be true'}
                </Typography>
              </Box>
            )}
            
            {/* Main condition inputs */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr auto', gap: 2, alignItems: 'center' }}>
              {/* Field */}
              <FormControl fullWidth>
                <InputLabel>Field</InputLabel>
                <Select
                  value={condition.field || ''}
                  onChange={(e) => updateCondition(index, 'field', e.target.value)}
                  label="Field"
                >
                  {Object.entries(RULE_FIELDS).map(([key, field]) => (
                    <MenuItem key={key} value={key}>
                      {field.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Operator */}
              <FormControl fullWidth disabled={!condition.field}>
                <InputLabel>Condition</InputLabel>
                <Select
                  value={condition.operator || ''}
                  onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                  label="Condition"
                >
                  {getAvailableOperators(condition.field).map((op) => (
                    <MenuItem key={op.value} value={op.value}>
                      {op.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Value */}
              <Box>
                {condition.field && condition.operator ? (
                  renderValueInput(condition, index)
                ) : (
                  <TextField
                    fullWidth
                    label="Value"
                    disabled
                    placeholder="Select field and condition first"
                  />
                )}
              </Box>

              {/* Delete button */}
              <IconButton
                onClick={() => removeCondition(index)}
                disabled={conditions.length === 1}
                color="error"
              >
                <Delete />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      ))}

      {/* Add condition button */}
      <Button
        variant="outlined"
        startIcon={<Add />}
        onClick={addCondition}
        sx={{ mb: 3 }}
      >
        Add Condition
      </Button>

      {/* Rule preview */}
      <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
        <Typography variant="body2" gutterBottom fontWeight="600">
          Rule Preview:
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#1976d2' }}>
          {generatePreview()}
        </Typography>
      </Paper>
    </Box>
  );
};

export default RuleBuilder; 